/**
 * Converts a hex color code to an RGB color value.
 *
 * @param {string} hex - The hex color code (e.g., "#FFFFFF").
 * @return {array} An array containing the RGB values [r, g, b].
 */
function hexToRgb(hex: string) {
  const bigint = parseInt(hex.slice(1), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return [r, g, b];
}

/**
 * Converts RGB color values to a hex color code.
 *
 * @param {number} r - The red component (0-255).
 * @param {number} g - The green component (0-255).
 * @param {number} b - The blue component (0-255).
 * @return {string} The hex color code (e.g., "#FFFFFF").
 */
function rgbToHex(r: number, g: number, b: number) {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

/**
 * Converts RGB color values to HSV color values.
 *
 * @param {number} r - The red component (0-255).
 * @param {number} g - The green component (0-255).
 * @param {number} b - The blue component (0-255).
 * @return {array} An array containing the HSV values [h, s, v].
 */
function rgbToHsv(r: number, g: number, b: number) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  const d = max - min;
  const s = max === 0 ? 0 : d / max;
  const v = max;
  let h = 1;
  if (max === min) {
    h = 0;
  } else {
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return [h, s, v];
}

/**
 * Converts HSV color values to RGB color values.
 *
 * @param {number} h - The hue component (0-1).
 * @param {number} s - The saturation component (0-1).
 * @param {number} v - The value component (0-1).
 * @return {array} An array containing the RGB values [r, g, b].
 */
function hsvToRgb(h: number, s: number, v: number) {
  let r = v,
    g = s,
    b = v;
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  switch (i % 6) {
    case 0:
      r = v;
      g = t;
      b = p;
      break;
    case 1:
      r = q;
      g = v;
      b = p;
      break;
    case 2:
      r = p;
      g = v;
      b = t;
      break;
    case 3:
      r = p;
      g = q;
      b = v;
      break;
    case 4:
      r = t;
      g = p;
      b = v;
      break;
    case 5:
      r = v;
      g = p;
      b = q;
      break;
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

/**
 * Calculates the complementary color for a given hex color code.
 *
 * @param {string} hex - The hex color code.
 * @return {string} The hex color code of the complementary color.
 */
function getComplementaryColor(hex: any) {
  const [r, g, b] = hexToRgb(hex);
  const [h, s, v] = rgbToHsv(r, g, b);
  const complementaryHue = (h + 0.5) % 1.0;
  const [cr, cg, cb] = hsvToRgb(complementaryHue, s, v);
  return rgbToHex(cr, cg, cb);
}

/**
 * Calculates two analogous colors for a given hex color code.
 *
 * @param {string} hex - The hex color code.
 * @param {number} angle - The angle for analogous colors (default is 30/360).
 * @return {array} An array containing the hex codes of the two analogous colors.
 */
function getAnalogousColors(hex: any, angle = 30 / 360) {
  const [r, g, b] = hexToRgb(hex);
  const [h, s, v] = rgbToHsv(r, g, b);
  const analogousHue1 = (h + angle) % 1.0;
  const analogousHue2 = (h - angle + 1.0) % 1.0;
  const [ar1, ag1, ab1] = hsvToRgb(analogousHue1, s, v);
  const [ar2, ag2, ab2] = hsvToRgb(analogousHue2, s, v);
  return [rgbToHex(ar1, ag1, ab1), rgbToHex(ar2, ag2, ab2)];
}

/**
 * Generates a color palette with the original color, its complementary color, and two analogous colors.
 *
 * @param {string} hex - The hex color code.
 * @return {object} An object containing the hex codes of the palette colors.
 */
export function generateColorPalette(hex: string) {
  const blob1 = hex;
  const blob2 = getComplementaryColor(hex);
  const [blob3, blob4] = getAnalogousColors(hex);
  return {
    "--blob-1": blob1,
    "--blob-2": blob2,
    "--blob-3": blob3,
    "--blob-4": blob4,
  };
}
